import EntityModel from '@/common/models/entity-model';
import SegmentModel from '@/common/models/segment-model';
import SegmentDataModel from '@/common/models/segment-data-model';
import SegmentPageModel from '@/common/models/segment-page-model';

export default class SegmentCollectionModel extends EntityModel {
    /**
     * @type {Object.<string, SegmentPageModel>}
     */
    $pages;

    /**
     * @type {SegmentPageModel}
     */
    $activePage;

    get pages() {
        return {...this.$pages};
    }

    get activePage() {
        if (!this.$activePage) {
            // Returns first non-empty page.
            return Object.values(this.pages).find(page => page.activeCategory !== null) || null;
        }

        return this.$activePage;
    }

    get hasStarredSegments() {
        return this.activePage.hasStarredSegments;
    }

    /**
     * Determine the default page name to display when missing.
     * Occurs when the active segment ID is not defined.
     * @returns {string|*}
     */
    // eslint-disable-next-line class-methods-use-this
    get defaultPageName() {
        return SegmentDataModel.Pages.lists;
    }

    get activeCategory() {
        const {activePage} = this;

        return activePage ? activePage.activeCategory : null;
    }

    get activeSegment() {
        const {activePage} = this;

        return activePage ? activePage.activeSegment : null;
    }

    hydrate(
        {
            segments = [],
            activePageName = null,
        } = {},
    ) {
        this.$pages = {};

        Object.values(SegmentDataModel.Pages).forEach((name) => {
            this.$pages[name] = new SegmentPageModel({name});
        });

        segments.forEach((segment) => {
            this.addSegment(segment);
        });

        if (activePageName) {
            this.selectPage(activePageName);
        } else {
            this.deselectPage();
        }
    }

    /**
     * @param {SegmentModel|{}} segment
     */
    addSegment(segment) {
        if (!(segment instanceof SegmentModel)) {
            segment = new SegmentModel(segment);
        }

        if (!this.$pages[segment.data.page]) {
            throw new Error('Invalid segment page');
        }

        this.$pages[segment.data.page].addSegment(segment);
    }

    /**
     * @param {string} segmentId
     */
    removeSegment(segmentId, update) {
        const segment = this.findSegmentById(segmentId);
        if (!(segment instanceof SegmentModel)) {
            throw new Error(`Segment not found ${segmentId}`);
        }

        const page = this.$pages[segment.data.page];
        page.removeSegment(segment.id, update);
    }

    updateSegment(segment, data) {
        const {activeSegment} = this;

        this.removeSegment(segment.id);
        this.addSegment({
            ...segment,
            ...data,
        });
        if (activeSegment && activeSegment.id === segment.id) {
            this.selectSegment(segment.id);
        }
    }

    deselectPage() {
        this.$activePage = null;
    }

    /**
     * @param {String} pageName
     */
    selectPage(pageName) {
        if (!this.$pages[pageName]) {
            throw new Error('Invalid active page');
        }

        const activePage = this.$pages[pageName];
        this.$activePage = activePage;
        activePage.deselectCategory();
    }

    findSegmentById(segmentId) {
        const segment = Object.values(this.$pages).reduce(
            (memo, page) => memo || page.findSegmentById(segmentId),
            null,
        );
        return segment || null;
    }

    selectSegment(segmentId) {
        const segment = this.findSegmentById(segmentId);

        if (!(segment instanceof SegmentModel)) {
            throw new Error('Segment not found');
        }
        this.selectPage(segment.data.page);
        this.activePage.selectSegment(segmentId);
    }
}
